import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { NetworkBadge } from '@/components/NetworkBadge';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  COMPANY_FILTER_OPTIONS,
  COMPANY_OPTIONS,
  SERVICE_SOURCE_PROVIDER_OPTIONS,
  SOURCE_PROVIDER_FILTER_OPTIONS,
  SOURCE_PROVIDER_OPTIONS,
  VISIBILITY_FILTER_OPTIONS,
} from '@/components/panelOptions';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  Input,
  Loading,
  Modal,
  Money,
  ProSearch,
  Select,
  StatusBadge,
  Textarea,
  VisibilitySelect,
} from '@/components/ui';
import { ShamBalanceSettingsPanel } from '@/screens/admin/ShamBalance';
import { formatNumber, str } from '@/utils/format';

const COMPANY_LABELS: Record<string, string> = Object.fromEntries(
  COMPANY_OPTIONS.map((o) => [o.value, o.label]),
);

const SOURCE_LABELS: Record<string, string> = Object.fromEntries(
  SOURCE_PROVIDER_OPTIONS.map((o) => [o.value, o.label]),
);

const KIND_LABELS: Record<string, string> = {
  units: 'وحدات',
  bills: 'فاتورة',
  cash: 'كاش',
};

const FALLBACK_SERVICES: JsonMap[] = [
  { slug: 'units', name: 'وحدات', pricing_mode: 'fixed', is_active: 1 },
  { slug: 'bills', name: 'فواتير', pricing_mode: 'variable', is_active: 1 },
  { slug: 'cash', name: 'كاش', pricing_mode: 'variable', is_active: 1 },
];

type MembershipPriceRow = {
  membership_id: number;
  id: number;
  name: string;
  fixed_profit_value: string;
  fixed_value: string;
};

type PackageForm = {
  id: number;
  company: string;
  operation_kind: string;
  source_provider: string;
  label: string;
  amount: string;
  sale_price: string;
  buy_price: string;
  min_amount: string;
  max_amount: string;
  sale_rate: string;
  buy_rate: string;
  is_active: number;
  sort_order: number;
};

const EMPTY_PACKAGE: PackageForm = {
  id: 0,
  company: 'syriatel',
  operation_kind: 'units',
  source_provider: 'kazeti_ussd',
  label: '',
  amount: '',
  sale_price: '',
  buy_price: '',
  min_amount: '',
  max_amount: '',
  sale_rate: '',
  buy_rate: '',
  is_active: 1,
  sort_order: 0,
};

function companyLabel(value: unknown): string {
  const key = str(value, '');
  return COMPANY_LABELS[key] ?? key;
}

function serviceLabel(pkg: JsonMap): string {
  const kind = str(pkg.operation_kind, 'units');
  return str(pkg.service_name, KIND_LABELS[kind] ?? kind);
}

function isFixedPackage(form: PackageForm, services: JsonMap[]): boolean {
  const svc = services.find((s) => str(s.slug) === str(form.operation_kind, 'units'));
  if (svc) return str(svc.pricing_mode) === 'fixed';
  return str(form.operation_kind, 'units') === 'units';
}

function isFixedRow(pkg: JsonMap): boolean {
  const mode = str(pkg.pricing_mode, '');
  if (mode === 'fixed' || mode === 'variable') return mode === 'fixed';
  return str(pkg.operation_kind, 'units') === 'units';
}

function defaultMembershipRows(memberships: JsonMap[]): MembershipPriceRow[] {
  return memberships.map((m) => ({
    membership_id: Number(m.id),
    id: Number(m.id),
    name: str(m.name),
    fixed_profit_value: '',
    fixed_value: '',
  }));
}

function packageLimitsText(pkg: JsonMap): string {
  const min = Number(pkg.min_amount ?? 0);
  const max = Number(pkg.max_amount ?? 0);
  if (min > 0 && max > 0) return `${formatNumber(min)} — ${formatNumber(max)}`;
  return '—';
}

type MembershipPricingModalProps = {
  itemId: number;
  itemName: string;
  fixedLabel: string;
  onClose: () => void;
  onSaved: (message: string, ok: boolean) => void;
};

function MembershipPricingModal({
  itemId,
  itemName,
  fixedLabel,
  onClose,
  onSaved,
}: MembershipPricingModalProps) {
  const [rows, setRows] = useState<JsonMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await admin.membershipPricing({
      item_type: 'recharge_package',
      item_id: itemId,
    });
    setLoading(false);
    if (!res.success) {
      onSaved(errorMessage(res), false);
      return;
    }
    setRows(pickList(res, 'data', 'items'));
  }, [itemId, onSaved]);

  useEffect(() => {
    void load();
  }, [load]);

  function patchRow(id: number, patch: JsonMap) {
    setRows((prev) => prev.map((r) => (Number(r.id) === id ? { ...r, ...patch } : r)));
  }

  function pricingModeForRow(row: JsonMap): 'fixed' | 'inherit' {
    if (Number(row.fixed_value || 0) > 0 || Number(row.fixed_profit_value || 0) > 0) return 'fixed';
    return 'inherit';
  }

  async function save() {
    setBusy(true);
    const res = await admin.saveMembershipPricing({
      item_type: 'recharge_package',
      item_id: itemId,
      prices: rows.map((row) => ({
        membership_id: row.id,
        pricing_mode: pricingModeForRow(row),
        percent_value: 0,
        fixed_value: row.fixed_value || 0,
        fixed_profit_value: row.fixed_profit_value || 0,
      })),
    });
    setBusy(false);
    const msg = res.success
      ? typeof res.message === 'string'
        ? res.message
        : 'تم حفظ أسعار العضويات.'
      : errorMessage(res);
    onSaved(msg, Boolean(res.success));
    if (res.success) onClose();
  }

  return (
    <Modal open title="أسعار العضويات" subtitle={itemName} onClose={onClose}>
      {loading ? (
        <Loading />
      ) : (
        <div className="membership-price-list">
          {rows.map((row) => (
            <article key={str(row.id)} className="membership-price-row">
              <div className="membership-price-head">
                <strong>{str(row.name)}</strong>
                <small>أدخل ربحاً ثابتاً أو سعر بيع نهائياً لهذه العضوية.</small>
              </div>
              <div className="form-grid two-columns tight">
                <Input
                  label="ربح ثابت لهذه العضوية"
                  value={str(row.fixed_profit_value, '')}
                  onChange={(e) => patchRow(Number(row.id), { fixed_profit_value: e.target.value })}
                  inputMode="decimal"
                  placeholder="مثال: 500"
                />
                <Input
                  label={fixedLabel}
                  value={str(row.fixed_value, '')}
                  onChange={(e) => patchRow(Number(row.id), { fixed_value: e.target.value })}
                  inputMode="decimal"
                  placeholder="اتركه فارغاً ليُحسب من التكلفة + الربح"
                />
              </div>
              <small className="form-hint">
                عند إدخال السعر النهائي يُعتمد مباشرة. وعند إدخال الربح فقط يُحسب سعر البيع من تكلفة
                الشراء مضافاً إليها الربح الثابت.
              </small>
            </article>
          ))}
          <div className="action-row wrap top-space">
            <Button type="button" disabled={busy} onClick={() => void save()}>
              حفظ أسعار العضويات
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              إلغاء
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function RechargePackagesScreen() {
  const [packages, setPackages] = useState<JsonMap[]>([]);
  const [services, setServices] = useState<JsonMap[]>([]);
  const [memberships, setMemberships] = useState<JsonMap[]>([]);
  const [form, setForm] = useState<PackageForm>(EMPTY_PACKAGE);
  const [membershipPrices, setMembershipPrices] = useState<MembershipPriceRow[]>([]);
  const [advancedPkg, setAdvancedPkg] = useState<JsonMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const activeServices = services.length ? services : FALLBACK_SERVICES;
  const fixedPricing = useMemo(
    () => isFixedPackage(form, activeServices),
    [form, activeServices],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    const [pkgRes, memRes, svcRes] = await Promise.all([
      admin.rechargePackages(),
      admin.memberships(),
      admin.ussdServices(),
    ]);
    setLoading(false);
    if (!pkgRes.success) {
      setError(errorMessage(pkgRes));
      return;
    }
    setPackages(pickList(pkgRes, 'data', 'packages', 'items'));
    if (memRes.success) {
      const memList = pickList(memRes, 'data', 'memberships', 'plans', 'items');
      setMemberships(memList);
      setMembershipPrices((prev) => (prev.length ? prev : defaultMembershipRows(memList)));
    }
    if (svcRes.success) {
      const list = pickList(svcRes, 'data', 'services', 'items');
      setServices(list.length ? list : FALLBACK_SERVICES);
    } else {
      setServices(FALLBACK_SERVICES);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return packages.filter((pkg) => {
      if (companyFilter !== 'all' && str(pkg.company) !== companyFilter) return false;
      if (kindFilter !== 'all' && str(pkg.operation_kind, 'units') !== kindFilter) return false;
      if (sourceFilter !== 'all' && str(pkg.source_provider) !== sourceFilter) return false;
      const active = Number(pkg.is_active ?? 1) === 1;
      if (statusFilter === 'active' && !active) return false;
      if (statusFilter === 'hidden' && active) return false;
      if (!needle) return true;
      const hay = [
        str(pkg.label ?? pkg.name),
        companyLabel(pkg.company),
        serviceLabel(pkg),
        SOURCE_LABELS[str(pkg.source_provider, '')] ?? str(pkg.source_provider),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [packages, q, companyFilter, kindFilter, sourceFilter, statusFilter]);

  const metrics = useMemo(() => {
    const active = packages.filter((p) => Number(p.is_active ?? 1) === 1).length;
    const syriatel = packages.filter((p) => str(p.company) === 'syriatel').length;
    const mtn = packages.filter((p) => str(p.company) === 'mtn').length;
    return { total: packages.length, active, syriatel, mtn };
  }, [packages]);

  function resetForm() {
    setForm(EMPTY_PACKAGE);
    setMembershipPrices(defaultMembershipRows(memberships));
    setMsg('');
  }

  function onOperationKindChange(kind: string) {
    setForm((prev) => ({
      ...prev,
      operation_kind: kind,
      amount: '',
      sale_price: '',
      buy_price: '',
      min_amount: '',
      max_amount: '',
      sale_rate: '',
      buy_rate: '',
    }));
  }

  function patchMembershipPrice(id: number, patch: Partial<MembershipPriceRow>) {
    setMembershipPrices((rows) =>
      rows.map((r) => (Number(r.membership_id || r.id) === id ? { ...r, ...patch } : r)),
    );
  }

  async function editPackage(pkg: JsonMap) {
    setForm({
      id: Number(pkg.id) || 0,
      company: str(pkg.company, 'syriatel'),
      operation_kind: str(pkg.operation_kind, 'units'),
      source_provider: str(pkg.source_provider, 'kazeti_ussd'),
      label: str(pkg.label ?? pkg.name),
      amount: str(pkg.amount, ''),
      sale_price: str(pkg.sale_price, ''),
      buy_price: str(pkg.buy_price, ''),
      min_amount: str(pkg.min_amount, ''),
      max_amount: str(pkg.max_amount, ''),
      sale_rate: str(pkg.sale_rate, ''),
      buy_rate: str(pkg.buy_rate, ''),
      is_active: Number(pkg.is_active ?? 1) ? 1 : 0,
      sort_order: Number(pkg.sort_order ?? 0),
    });
    const res = await admin.membershipPricing({
      item_type: 'recharge_package',
      item_id: Number(pkg.id),
    });
    const rows = res.success ? pickList(res, 'data', 'items') : [];
    if (rows.length) {
      setMembershipPrices(
        rows.map((row) => ({
          membership_id: Number(row.id),
          id: Number(row.id),
          name: str(row.name),
          fixed_profit_value: str(row.fixed_profit_value, ''),
          fixed_value: str(row.fixed_value, ''),
        })),
      );
    } else {
      setMembershipPrices(defaultMembershipRows(memberships));
    }
    setMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function savePackage() {
    if (!form.label.trim()) {
      setMsg('اسم الفئة مطلوب.');
      return;
    }
    setBusy(true);
    const body: JsonMap = {
      ...form,
      name: form.label,
      membership_prices: membershipPrices.map((row) => ({
        membership_id: row.membership_id || row.id,
        fixed_profit_value: row.fixed_profit_value || 0,
        fixed_value: row.fixed_value || 0,
      })),
    };
    if (!fixedPricing) {
      body.amount = '0';
      body.sale_price = '0';
      body.buy_price = '0';
    }
    const res = await admin.saveRechargePackage(body);
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم حفظ الفئة والتسعير.' : 'تعذر الحفظ.'));
    if (res.success) {
      resetForm();
      void loadAll();
    }
  }

  async function remove(id: unknown) {
    if (!window.confirm('حذف الفئة؟')) return;
    const res = await admin.deleteRechargePackage({ id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    void loadAll();
  }

  const serviceOptions = activeServices.filter(
    (s) => Number(s.is_active ?? 1) === 1 || str(s.slug) === str(form.operation_kind),
  );

  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={() => void loadAll()} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="package"
      title="فئات الشحن"
      subtitle="إدارة فئات خدمات USSD وتسعيرها، مع إضافة خدمات جديدة وتشغيل كل خدمة بشكل مستقل."
      actions={
        <Button type="button" variant="secondary" onClick={() => void loadAll()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="كل الفئات" value={String(metrics.total)} icon={<Icon name="package" size={21} />} />
        <MetricCard title="مفعلة" value={String(metrics.active)} icon={<Icon name="check" size={21} />} />
        <MetricCard title="سيريتل" value={String(metrics.syriatel)} icon={<Icon name="deposit" size={21} />} />
        <MetricCard title="MTN" value={String(metrics.mtn)} icon={<Icon name="zap" size={21} />} />
      </MetricGrid>

      <ShamBalanceSettingsPanel onFlash={setMsg} />
      <ServiceControlsPanel onFlash={setMsg} />

      <SectionCard
        title="فئات الشحن والتسعير"
        subtitle="إدارة فئات الخدمات وأسعارها ونسب ربح العضويات."
        icon={<Icon name="package" size={24} />}
      >
        <Form
          onSubmit={() => {
            void savePackage();
          }}
        >
          <div className="form-grid two-columns">
            <Select
              label="الشركة"
              value={form.company}
              onChange={(v) => setForm((p) => ({ ...p, company: v }))}
              options={COMPANY_OPTIONS}
            />
            <Select
              label="الخدمة"
              value={form.operation_kind}
              onChange={onOperationKindChange}
              options={serviceOptions.map((s) => ({
                value: str(s.slug),
                label: `${str(s.name)} — ${str(s.pricing_mode) === 'fixed' ? 'فئات ثابتة' : 'قيمة حرة'}`,
              }))}
            />
            <Select
              label="مصدر التنفيذ"
              value={form.source_provider}
              onChange={(v) => setForm((p) => ({ ...p, source_provider: v }))}
              options={SOURCE_PROVIDER_OPTIONS}
            />
            <Input
              label="اسم الفئة"
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder={fixedPricing ? 'مثال: سيريتل 10000' : 'مثال: فاتورة سيريتل'}
              required
            />
            {fixedPricing ? (
              <>
                <Input
                  label="قيمة الشحن"
                  value={form.amount}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                  inputMode="decimal"
                />
                <Input
                  label="سعر بيع افتراضي"
                  value={form.sale_price}
                  onChange={(e) => setForm((p) => ({ ...p, sale_price: e.target.value }))}
                  inputMode="decimal"
                />
                <Input
                  label="تكلفة الشراء"
                  value={form.buy_price}
                  onChange={(e) => setForm((p) => ({ ...p, buy_price: e.target.value }))}
                  inputMode="decimal"
                />
              </>
            ) : (
              <>
                <Input
                  label="الحد الأدنى"
                  value={form.min_amount}
                  onChange={(e) => setForm((p) => ({ ...p, min_amount: e.target.value }))}
                  inputMode="decimal"
                />
                <Input
                  label="الحد الأعلى"
                  value={form.max_amount}
                  onChange={(e) => setForm((p) => ({ ...p, max_amount: e.target.value }))}
                  inputMode="decimal"
                />
                <Input
                  label="سعر بيع افتراضي"
                  value={form.sale_rate}
                  onChange={(e) => setForm((p) => ({ ...p, sale_rate: e.target.value }))}
                  inputMode="decimal"
                  placeholder="مثال: 1.05"
                />
                <Input
                  label="سعر الليرة شراء"
                  value={form.buy_rate}
                  onChange={(e) => setForm((p) => ({ ...p, buy_rate: e.target.value }))}
                  inputMode="decimal"
                  placeholder="مثال: 1"
                />
              </>
            )}
            <VisibilitySelect
              value={form.is_active}
              onChange={(v) => setForm((p) => ({ ...p, is_active: Number(v) ? 1 : 0 }))}
            />
            <Input
              label="الترتيب"
              value={String(form.sort_order)}
              onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) || 0 }))}
              inputMode="numeric"
            />
          </div>

          <div className="membership-inline-pricing top-space">
            <div className="section-mini-title">
              <strong>تسعير العضويات أثناء الإضافة</strong>
              <small>
                {fixedPricing
                  ? 'ضع ربح ثابت للفئة أو سعر بيع نهائي لكل عضوية.'
                  : 'ضع ربح ثابت على سعر الليرة أو سعر ليرة نهائي لكل عضوية.'}
              </small>
            </div>
            {membershipPrices.length === 0 ? (
              <Empty title="لا عضويات" hint="أضف عضوية أولاً من صفحة العضويات." />
            ) : (
              <div className="membership-price-list compact-pricing-list">
                {membershipPrices.map((row) => (
                  <article key={String(row.membership_id || row.id)} className="membership-price-row">
                    <div className="membership-price-head">
                      <strong>{row.name}</strong>
                      <small>اترك الحقول فارغة لتستخدم السعر الافتراضي.</small>
                    </div>
                    <div className="form-grid two-columns tight">
                      <Input
                        label={fixedPricing ? 'ربح ثابت للفئة' : 'ربح ثابت على سعر الليرة'}
                        value={row.fixed_profit_value}
                        onChange={(e) =>
                          patchMembershipPrice(row.membership_id || row.id, {
                            fixed_profit_value: e.target.value,
                          })
                        }
                        inputMode="decimal"
                        placeholder={fixedPricing ? 'مثال: 500' : 'مثال: 0.05'}
                      />
                      <Input
                        label={fixedPricing ? 'سعر البيع النهائي' : 'سعر الليرة النهائي'}
                        value={row.fixed_value}
                        onChange={(e) =>
                          patchMembershipPrice(row.membership_id || row.id, {
                            fixed_value: e.target.value,
                          })
                        }
                        inputMode="decimal"
                        placeholder="اختياري"
                      />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="action-row wrap top-space">
            <Button type="submit" disabled={busy}>
              <Icon name="check" size={18} />{' '}
              {form.id ? 'حفظ الفئة والتسعير' : 'إضافة الفئة مع التسعير'}
            </Button>
            {form.id ? (
              <Button type="button" variant="secondary" onClick={resetForm}>
                إلغاء التعديل
              </Button>
            ) : null}
          </div>
          <FlashMessage message={msg} />
        </Form>
      </SectionCard>

      <FilterCard title="تصفية الفئات" icon={<Icon name="search" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder="بحث باسم الفئة أو الشبكة أو الخدمة" />
        <Select
          label="الشركة"
          value={companyFilter}
          onChange={setCompanyFilter}
          options={COMPANY_FILTER_OPTIONS}
        />
        <Select
          label="الخدمة"
          value={kindFilter}
          onChange={setKindFilter}
          options={[
            { value: 'all', label: 'الكل' },
            ...activeServices.map((s) => ({ value: str(s.slug), label: str(s.name) })),
          ]}
        />
        <Select
          label="المصدر"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={SOURCE_PROVIDER_FILTER_OPTIONS}
        />
        <Select
          label="الحالة"
          value={statusFilter}
          onChange={setStatusFilter}
          options={VISIBILITY_FILTER_OPTIONS}
        />
      </FilterCard>

      <SectionCard title={`الفئات الحالية (${filtered.length})`} icon={<Icon name="chart" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا فئات مطابقة" hint="عدّل التصفية أو أضف فئة من النموذج أعلاه." />
        ) : (
          <div className="list-stack">
            {filtered.map((pkg) => {
              const fixed = isFixedRow(pkg);
              const active = Number(pkg.is_active ?? 1) === 1;
              return (
                <article key={str(pkg.id)} className="list-card compact">
                  <div className="list-head">
                    <div>
                      <strong>{str(pkg.label ?? pkg.name)}</strong>
                      <small>
                        {companyLabel(pkg.company)} — {serviceLabel(pkg)} —{' '}
                        {SOURCE_LABELS[str(pkg.source_provider, '')] ?? str(pkg.source_provider)}
                      </small>
                    </div>
                    <div className="list-end">
                      <NetworkBadge company={pkg.company} />
                      <StatusBadge status={active ? 'active' : 'hidden'} />
                    </div>
                  </div>
                  {fixed ? (
                    <p>
                      السعر: <Money value={pkg.sale_price} /> — القيمة:{' '}
                      <Money value={pkg.amount} /> — التكلفة: <Money value={pkg.buy_price ?? 0} />
                    </p>
                  ) : (
                    <p>
                      الحدود: {packageLimitsText(pkg)} — بيع الليرة:{' '}
                      {formatNumber(pkg.sale_rate ?? 0)} — شراء: {formatNumber(pkg.buy_rate ?? 0)}
                    </p>
                  )}
                  <div className="action-row wrap">
                    <Button type="button" variant="secondary" onClick={() => void editPackage(pkg)}>
                      تعديل
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => setAdvancedPkg(pkg)}>
                      أسعار العضويات المتقدمة
                    </Button>
                    <Button type="button" variant="danger" onClick={() => void remove(pkg.id)}>
                      حذف
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>

      {advancedPkg ? (
        <MembershipPricingModal
          itemId={Number(advancedPkg.id)}
          itemName={str(advancedPkg.label ?? advancedPkg.name)}
          fixedLabel={
            isFixedRow(advancedPkg) ? 'سعر البيع النهائي للفئة' : 'سعر الوحدة النهائي'
          }
          onClose={() => setAdvancedPkg(null)}
          onSaved={(message, ok) => {
            if (!ok) window.alert(message);
            else setMsg(message);
          }}
        />
      ) : null}
    </ScreenFrame>
  );
}

function ServiceControlsPanel({ onFlash }: { onFlash?: (message: string) => void }) {
  const state = useAsync(
    () => admin.serviceControls(),
    (p) => pickList(p, 'data', 'controls', 'services', 'items'),
  );
  const [rows, setRows] = useState<JsonMap[]>([]);
  const [busyKey, setBusyKey] = useState('');

  useEffect(() => {
    if (state.data) setRows(state.data);
  }, [state.data]);

  function rowKey(row: JsonMap): string {
    return `${str(row.company)}:${str(row.operation_kind)}`;
  }

  function serviceTitle(row: JsonMap): string {
    return `${companyLabel(row.company)} — ${str(row.service_name, KIND_LABELS[str(row.operation_kind)] ?? str(row.operation_kind))}`;
  }

  function patchRow(id: unknown, patch: JsonMap) {
    setRows((prev) =>
      prev.map((r) => (Number(r.id) === Number(id) ? { ...r, ...patch } : r)),
    );
  }

  async function saveRow(row: JsonMap, nextActive?: number) {
    const key = rowKey(row);
    setBusyKey(key);
    const isActive = nextActive ?? Number(row.is_active ?? 0);
    const res = await admin.saveServiceControls({
      company: row.company,
      operation_kind: row.operation_kind,
      source_provider: str(row.source_provider, 'kazeti_ussd'),
      is_active: isActive ? 1 : 0,
      disabled_message: str(row.disabled_message, ''),
    });
    setBusyKey('');
    const message = res.success
      ? str(res.message, isActive ? 'تم تشغيل الخدمة.' : 'تم إيقاف الخدمة.')
      : errorMessage(res);
    onFlash?.(message);
    if (res.success) {
      setRows((prev) =>
        prev.map((r) =>
          Number(r.id) === Number(row.id)
            ? { ...r, is_active: isActive ? 1 : 0, source_provider: str(row.source_provider, 'kazeti_ussd') }
            : r,
        ),
      );
      state.reload();
    }
  }

  if (state.loading) return <Loading />;
  if (state.error) {
    return <ErrorBox message={state.error} onRetry={state.reload} />;
  }

  const list = rows.length ? rows : (state.data ?? []);

  return (
    <SectionCard
      title="إدارة توفر الخدمات"
      subtitle="فعّل كل خدمة واختر مصدر التنفيذ: Sham Balance أو USSD."
      icon={<Icon name="settings" size={24} />}
      className="service-controls-section"
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      {list.length === 0 ? (
        <Empty title="لا إعدادات" hint="تعذر تحميل قائمة الخدمات." />
      ) : (
        <div className="service-control-grid">
          {list.map((row) => {
            const active = Number(row.is_active ?? 0) === 1;
            const key = rowKey(row);
            const saving = busyKey === key;
            const provider = str(row.source_provider, 'kazeti_ussd');
            return (
              <article
                key={key}
                className={`service-control-card ${active ? 'active' : 'disabled'}`.trim()}
              >
                <div className="service-control-head">
                  <div>
                    <strong>{serviceTitle(row)}</strong>
                    <small>{active ? 'متاحة للمستخدمين' : 'غير متاحة حالياً'}</small>
                  </div>
                  <span className={`service-status-badge ${active ? 'on' : 'off'}`.trim()}>
                    {active ? 'مفعلة' : 'متوقفة'}
                  </span>
                </div>
                <Select
                  label="مصدر التنفيذ"
                  value={provider}
                  onChange={(v) => patchRow(row.id, { source_provider: v })}
                  options={SERVICE_SOURCE_PROVIDER_OPTIONS}
                />
                <Textarea
                  className="service-message-field"
                  label="رسالة التوقف"
                  value={str(row.disabled_message, '')}
                  onChange={(v) => patchRow(row.id, { disabled_message: v })}
                  placeholder="أدخل رسالة مختصرة تظهر عند توقف الخدمة"
                />
                <div className="service-control-actions">
                  <Button
                    type="button"
                    variant={active ? 'danger' : 'primary'}
                    disabled={saving}
                    onClick={() => void saveRow(row, active ? 0 : 1)}
                  >
                    {active ? 'إيقاف الخدمة' : 'تفعيل الخدمة'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saving}
                    onClick={() => void saveRow(row)}
                  >
                    حفظ
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

export function ServiceControlsScreen() {
  const [flash, setFlash] = useState('');

  return (
    <ScreenFrame
      mode="admin"
      icon="settings"
      title="إدارة توفر الخدمات"
      subtitle="يمكن تفعيل كل خدمة أو إيقافها بشكل مستقل."
    >
      {flash ? <p className={flash.includes('تم') ? 'ok' : 'error'}>{flash}</p> : null}
      <ServiceControlsPanel onFlash={setFlash} />
    </ScreenFrame>
  );
}
